import { Container, Loader, Progress } from "taltech-styleguide";
import { WizardProvider, useWizard } from "./context";
import { WizardProps } from "./types";
import { NextBtn, PreviousBtn } from "./ProgressButtons";
import { useCurrentStep, useProgressIndicator } from "./use-wizard.hook";
import { StepPanel } from "./StepPanel";
import { MutableRefObject, useEffect, useRef, useState } from "react";
import { WizardState } from "./types";
import { isEmpty } from "ramda";
import { useLoading } from "../../../../components-common/_shared/loading/use-loading.hook";
import { initialRequests } from "./helpers";
import { useNotifications } from "@hooks";
import { IUserNotificationService } from "@dirootie-web/components-types";

type ProgressIndicatorProps = {
  stepIndex: number;
  steps: string[];
};
export const ProgressIndicator = ({
  stepIndex,
  steps,
}: ProgressIndicatorProps) => {
  return (
    <Progress
      aria-label="Progress"
      steps={steps}
      position={"center"}
      currentStep={stepIndex}
      onStepChange={(index: number, prevIndex: number) => {}}
    />
  );
};

type WizardControlBarProps = {
  steps: string[];
  stepIndex: number;
  previousBtn: {
    onClick: (state: WizardState, dispatch: any) => void;
    disabled: boolean;
  };
  nextBtn: {
    onClick: (state: WizardState, dispatch: any) => void;
    disabled: boolean;
  };
  cancelBtn: {
    onClick: (state: WizardState, dispatch: any) => void;
    disabled: boolean;
  };
};

export const WizardControlBar = ({
  steps,
  stepIndex,
  previousBtn,
  nextBtn,
  cancelBtn,
}: WizardControlBarProps) => {
  const wizard = useWizard();
  // const { onClick = () => { return Promise.resolve();}, ...rest } = nextBtn;
  return (
    <>
      <PreviousBtn
        {...previousBtn}
        onClick={() => {
          previousBtn.onClick(wizard.state, wizard.dispatch);
        }}
      />
      <ProgressIndicator {...{ steps, stepIndex }} />
      <div className="d-flex gap-3">
        {/* <ConfirmationActionButton kind="cancel"  /> */}
        <NextBtn
          {...nextBtn}
          onClick={() => {
            nextBtn.onClick(wizard.state, wizard.dispatch);
          }}
        />
        {/* //{...rest} onClick={() => { (onClick() as any).then((x:any) => wizard.dispatch("NEXT_STEP"));}}/> */}
      </div>
    </>
  );
};

export const ProgressWizardInner = () => {
  const indicatorProps = useProgressIndicator() as any;
  const { state } = useWizard();
  const { initial } = useCurrentStep();
  const currentStep: any = state.steps[state.stepIndex];
  if (!isEmpty(currentStep.data)) {
    if (state.stepIndex === 0) {
      currentStep.component.props.element.info = currentStep.data;
    } else if (state.stepIndex === 1) {
      currentStep.component.props.element.layout = currentStep.data;
    } else if (state.stepIndex === 2) {
      currentStep.component.props.element.elementTypes = currentStep.data;
    } else if (state.stepIndex === 3) {
      currentStep.component.props.element.configInformation = currentStep.data;
    }
  }
  const className =
    "d-flex align-items-start justify-content-between mb-5 mt-5";
  return (
    <>
      <div className={className}>
        <WizardControlBar {...indicatorProps} />
      </div>
      <hr />
      <Container>
        <h3>{initial.title}</h3>
        <StepPanel {...(state.steps[state.stepIndex] as any)} />
      </Container>
    </>
  );
};

const createInitialState = (
  props: WizardProps,
  formRefs: MutableRefObject<never[]>,
  isSubmitting: boolean,
  setIsSubmitting: (submitting: boolean) => void,
  userNotifications: IUserNotificationService
) => {
  const wizardSteps = props.steps.map((step, index) => ({
    component: step.component,
    title: step.title,
    titleShort: step.titleShort,
    loading: false,
    complete: false,
    props: {},
    data: {},
    index,
  }));
  const initialState = {
    stepIndex: 0,
    step: wizardSteps[0],
    lastValidStep: 0,
    previousBtn: {
      disabled: true,
      onClick: (state: WizardState, dispatch: any) => {
        dispatch({ type: "PREVIOUS_STEP" });
      },
    },
    submitBtn: {
      disabled: false,
      onClick: async (state: WizardState, dispatch: any) => {
        if (isSubmitting) return;

        setIsSubmitting(true);
        await props.onSubmit!(state).then((res: any) => {
          if (res.statusCode === undefined || res.statusCode !== 200) {
            userNotifications.error({ title: res.response.data.detail });
          }
            
          setIsSubmitting(false);
        });
      },
    },
    cancelBtn: { disabled: true },
    nextBtn: {
      disabled: false,
      onClick: (state: WizardState) => {
        if (formRefs.current[state.stepIndex]) {
          return (formRefs.current[state.stepIndex] as any).handleSubmit();
        } else {
          return Promise.resolve();
        }
      },
    },
    steps: wizardSteps,
    initialSteps: wizardSteps,
    refs: formRefs,
    elementTypesOptions: [],
    spaceFunctions: [],
    buildingParameters: [],
    isInCourseAssignment: false,
  };
  return initialState;
};

export const ProgressWizard = (props: WizardProps) => {
  const formRefs = useRef([]);
  const [isLoading, setIsLoading] = useLoading(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const userNotifications = useNotifications();
  const [initialState, setInitialState] = useState(
    createInitialState(
      props,
      formRefs,
      isSubmitting,
      setIsSubmitting,
      userNotifications
    )
  );
  useEffect(() => {
    initialRequests(
      initialState,
      setIsLoading,
      setInitialState,
      props.isInCourseAssignment
    );
  }, []);
  const wizard = isLoading ? (
    <Loader overlay></Loader>
  ) : (
    <WizardProvider initialState={initialState}>
      {isSubmitting ? <Loader overlay></Loader> : <ProgressWizardInner />}
    </WizardProvider>
  );
  return wizard;
};
